import {
  FREE_RIGHTS,
  LIFETIME_PRODUCT_ID,
  evaluateCommerceAvailability,
  rightsFor,
  type CommerceAvailabilityReason,
  type CommerceChannel,
  type CommerceRuntimePolicy,
  type EntitlementState,
  type Rights,
} from '../../domain/entitlement';

export type PurchaseChannel = CommerceChannel;

export interface PurchaseProvider {
  createOrder(productId: typeof LIFETIME_PRODUCT_ID): Promise<{ orderId: string; state: 'pending' }>;
  restore(productId: typeof LIFETIME_PRODUCT_ID): Promise<{ state: EntitlementState }>;
  readEntitlement(productId: typeof LIFETIME_PRODUCT_ID): Promise<{ state: EntitlementState }>;
}

export interface PurchaseAdapter {
  channel: PurchaseChannel;
  productId: typeof LIFETIME_PRODUCT_ID;
  available: boolean;
  unavailableReason: CommerceAvailabilityReason;
  rightsBeforeReadback: Rights;
  start(): Promise<{ orderId: string; state: 'pending' }>;
  restore(): Promise<Rights>;
  readRights(): Promise<Rights>;
}

export class PurchaseUnavailableError extends Error {
  readonly code = 'PURCHASE_UNAVAILABLE';
  constructor(readonly reason: CommerceAvailabilityReason) {
    super('구매 제공자를 확인할 수 없어 무료 기능으로 계속합니다.');
  }
}

export function createPurchaseAdapter(options: {
  channel: PurchaseChannel;
  policy: CommerceRuntimePolicy;
  provider?: PurchaseProvider;
}): PurchaseAdapter {
  const availability = evaluateCommerceAvailability(options.policy);
  const available = availability.available && Boolean(options.provider);
  const unavailableReason = available ? 'ready' : availability.reason === 'ready' ? 'provider_disabled' : availability.reason;
  const provider = () => {
    if (!available || !options.provider) throw new PurchaseUnavailableError(unavailableReason);
    return options.provider;
  };

  return {
    channel: options.channel,
    productId: LIFETIME_PRODUCT_ID,
    available,
    unavailableReason,
    rightsBeforeReadback: FREE_RIGHTS,
    async start() {
      return provider().createOrder(LIFETIME_PRODUCT_ID);
    },
    async restore() {
      return rightsFor((await provider().restore(LIFETIME_PRODUCT_ID)).state);
    },
    async readRights() {
      return rightsFor((await provider().readEntitlement(LIFETIME_PRODUCT_ID)).state);
    },
  };
}

export const COMMERCE_SAFE_OFF_POLICY: CommerceRuntimePolicy = {
  providerEnabled: false,
  productObserved: false,
  providerHealthy: false,
  killSwitch: true,
};
